import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { AuthLayout, AuthInput, AuthButton } from './AuthComponents';
import { REQUEST_PASSWORD_RESET } from '@/graphql/mutations';
import { ErrorBanner } from '@/components/ui/ErrorBanner';

export function ForgotPassword() {
    const navigate = useNavigate();
    const { t } = useTranslation('auth');
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const [requestReset, { loading }] = useMutation(REQUEST_PASSWORD_RESET);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email) {
            setError(t('forgotPassword.error.emailRequired'));
            return;
        }

        try {
            await requestReset({ variables: { input: { email } } });
            setSuccess(true);
        } catch {
            setError(t('forgotPassword.error.generic'));
        }
    };

    return (
        <AuthLayout
            title={t('forgotPassword.title')}
            subtitle={t('forgotPassword.subtitle')}
            footer={
                <button
                    onClick={() => navigate('/login')}
                    className="text-emerald-600 font-bold hover:underline inline-flex items-center gap-1 text-sm"
                >
                    <ArrowLeft size={16} />
                    {t('forgotPassword.backToLogin')}
                </button>
            }
        >
            {success ? (
                <div className="text-center space-y-4 py-4">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle className="text-emerald-600" size={32} />
                    </div>
                    <p className="text-slate-600 font-medium leading-relaxed">
                        {t('forgotPassword.success')}
                    </p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                    <AuthInput
                        label={t('forgotPassword.email.label')}
                        icon={Mail}
                        type="email"
                        placeholder={t('forgotPassword.email.placeholder')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />

                    {error && <ErrorBanner message={error} className="animate-in shake" />}

                    <AuthButton type="submit" isLoading={loading}>
                        {t('forgotPassword.submit')} <Send size={18} />
                    </AuthButton>
                </form>
            )}
        </AuthLayout>
    );
}
